import React, { useEffect, useState } from 'react'
import { Button, Table, TablePaginationConfig, Tag, Tooltip } from 'antd'
import moment from 'moment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { TemplateStatus } from 'types'
import { EyeOutlined } from '@ant-design/icons'
import { getTemplatesAction } from 'slices/template'
import { Link } from 'react-router-dom'

interface PropTypes {
	selectedStatus: string
	client?: string
}

const TemplateTable: React.FC<PropTypes> = ({ selectedStatus, client }) => {
	const { records, currentPage, totalCount, loading } = useAppSelector(
		state => state.template
	)

	const [pageSize, setPageSize] = useState(10)

	/**
	 * Pagination config for the table
	 */
	const pagination = {
		current: currentPage,
		total: totalCount,
		pageSize,
	}

	const dispatch = useAppDispatch()

	useEffect(() => {
		const options = {
			page: pagination.current,
			size: pagination.pageSize,
			status: selectedStatus,
			client,
		}
		dispatch(getTemplatesAction(options))
	}, [dispatch, selectedStatus, client])

	/**
	 * Handles pagination change by changing the current page in the store.
	 *
	 * @param page - Pagination object from the table
	 */

	const handlePageChange = (page: TablePaginationConfig) => {
		const options = {
			page: page.current || 1,
			size: page.pageSize || pageSize,
			status: selectedStatus,
			client,
		}
		setPageSize(page.pageSize || pageSize)
		dispatch(getTemplatesAction(options))
	}

	/**
	 * Columns for the Template table
	 */
	const columns = [
		{
			title: 'Label',
			dataIndex: 'label',
		},
		{
			title: 'Name',
			dataIndex: 'elementName',
		},
		{
			title: 'Category',
			dataIndex: 'category',
		},
		{
			title: 'Client Organization',
			dataIndex: ['client', 'organizationName'],
		},
		{
			title: 'Client Email',
			dataIndex: ['client', 'email'],
		},
		{
			title: 'Type',
			dataIndex: 'type',
		},
		{
			title: 'Status',
			dataIndex: 'status',
			render: (status: TemplateStatus) => {
				switch (status) {
					case 'DRAFT':
						return <Tag color='blue'>Draft</Tag>
					case 'PENDING':
						return <Tag color='cyan'>Pending</Tag>
					case 'CREATE_REQUEST':
						return <Tag color='purple'>Create Request</Tag>
					case 'REQUEST_TO_APPROVE':
						return <Tag color='geekblue'>Request To Approve</Tag>
					case 'APPROVED':
						return <Tag color='green'>Approved</Tag>
					case 'REJECTED':
						return <Tag color='volcano'>Rejected</Tag>
					case 'DELETE_REQUEST':
						return <Tag color='orange'>Delete Request</Tag>
					case 'DELETED':
						return <Tag color='red'>Deleted</Tag>
					default:
						return <Tag color='blue'>{status}</Tag>
				}
			},
		},
		{
			title: 'Remarks',
			dataIndex: 'remarks',
			render: (remarks: string) => remarks || '--',
		},
		{
			title: 'Created At',
			dataIndex: 'createdAt',
			render: (createdAt: string) => moment(createdAt).format('DD/MM/YYYY'),
		},
		{
			title: 'Action',
			key: 'action',
			dataIndex: '_id',
			render: (id: string) => (
				<div style={{ display: 'flex' }}>
					<div>
						<Tooltip placement='bottomLeft' title='View'>
							<Link to={`/templates/${id}`}>
								<Button type='primary' icon={<EyeOutlined />} />
							</Link>
						</Tooltip>
					</div>
				</div>
			),
		},
	]

	return (
		<Table
			columns={columns}
			rowKey={record => record._id}
			dataSource={records}
			pagination={{ ...pagination, position: ['bottomCenter'] }}
			loading={loading}
			onChange={handlePageChange}
			scroll={{ x: 'fit-content' }}
		/>
	)
}

TemplateTable.defaultProps = {
	client: undefined,
}

export default TemplateTable
